// Timezone-aware time helpers for the venue booking engine. Everything is
// anchored to a venue's IANA timeZone (e.g. "Asia/Kolkata"). We avoid date-fns-tz
// and use the platform Intl.DateTimeFormat machinery instead.

const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Numeric components of a moment rendered in a specific IANA zone.
export interface ZonedComponents {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
  hour: number; // 0-23
  minute: number; // 0-59
  second: number; // 0-59
  weekday: number; // 0=Sun..6=Sat
}

const componentFormatterCache = new Map<string, Intl.DateTimeFormat>();

function getComponentFormatter(timeZone: string) {
  let cached = componentFormatterCache.get(timeZone);
  if (!cached) {
    cached = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      weekday: "short",
    });
    componentFormatterCache.set(timeZone, cached);
  }
  return cached;
}

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

export function getZonedComponents(
  date: Date,
  timeZone: string,
): ZonedComponents {
  const parts = getComponentFormatter(timeZone).formatToParts(date);
  const lookup: Record<string, string> = {};
  for (const part of parts) {
    if (part.type !== "literal") lookup[part.type] = part.value;
  }
  return {
    year: Number(lookup.year),
    month: Number(lookup.month),
    day: Number(lookup.day),
    hour: Number(lookup.hour),
    minute: Number(lookup.minute),
    second: Number(lookup.second),
    weekday: WEEKDAY_INDEX[lookup.weekday] ?? 0,
  };
}

export function getZoneOffsetMinutes(date: Date, timeZone: string): number {
  const components = getZonedComponents(date, timeZone);
  const asUtc = Date.UTC(
    components.year,
    components.month - 1,
    components.day,
    components.hour,
    components.minute,
    components.second,
  );
  return Math.round((asUtc - date.getTime()) / MS_PER_MINUTE);
}

// Converts a wall-clock moment in the given zone to an absolute instant.
// DST fold is resolved by iterating twice — the naive offset lookup on the
// tentative UTC guess yields the zone's offset at that instant.
export function zonedWallClockToInstant(
  timeZone: string,
  wallClock: {
    year: number;
    month: number; // 1-12
    day: number; // 1-31
    hour?: number;
    minute?: number;
    second?: number;
  },
): Date {
  const asIfUtc = Date.UTC(
    wallClock.year,
    wallClock.month - 1,
    wallClock.day,
    wallClock.hour ?? 0,
    wallClock.minute ?? 0,
    wallClock.second ?? 0,
  );
  let guess = new Date(asIfUtc);
  for (let pass = 0; pass < 2; pass += 1) {
    const offsetMin = getZoneOffsetMinutes(guess, timeZone);
    const next = new Date(asIfUtc - offsetMin * MS_PER_MINUTE);
    if (next.getTime() === guess.getTime()) break;
    guess = next;
  }
  return guess;
}

export function minutesSinceZonedMidnight(
  date: Date,
  timeZone: string,
): number {
  const components = getZonedComponents(date, timeZone);
  return components.hour * 60 + components.minute;
}

export function startOfZonedDay(date: Date, timeZone: string): Date {
  const components = getZonedComponents(date, timeZone);
  return zonedWallClockToInstant(timeZone, {
    year: components.year,
    month: components.month,
    day: components.day,
  });
}

export function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * MS_PER_MINUTE);
}

export function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * MS_PER_DAY);
}

export function minutesBetween(start: Date, end: Date): number {
  return Math.round((end.getTime() - start.getTime()) / MS_PER_MINUTE);
}

export function rangesOverlap(
  aStart: Date,
  aEnd: Date,
  bStart: Date,
  bEnd: Date,
): boolean {
  return aStart.getTime() < bEnd.getTime() && bStart.getTime() < aEnd.getTime();
}

/** "6:00 PM" in the venue's own zone. */
export function clockLabel(at: Date, timeZone: string): string {
  return at.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone,
  });
}

/**
 * A window this long is an open-entry pass, not a start time.
 *
 * Six hours is the shortest window nobody would describe as "a 7am session":
 * a class, a court hour, or a coached block is at most three or four, while an
 * entry pass (open bouldering, pool entry, gym walk-in) runs the venue's
 * trading day. Below the threshold the start is the useful fact; above it, the
 * start alone actively misleads — it reads as "be there at 7" for a pass you
 * may use at any point until close.
 */
const ALL_DAY_WINDOW_MINUTES = 6 * 60;

export function isAllDayWindow(start: Date, end: Date): boolean {
  return minutesBetween(start, end) >= ALL_DAY_WINDOW_MINUTES;
}

/**
 * What a compact chip should call this window — "All day" for an open-entry
 * pass, the start time for anything you actually have to turn up for.
 *
 * Rendered server-side, in the venue's zone, for the same reason `clockLabel`
 * is: a device-zone clock hydrates differently from the server HTML.
 */
export function slotWindowLabel(
  start: Date,
  end: Date,
  timeZone: string,
): string {
  return isAllDayWindow(start, end) ? "All day" : clockLabel(start, timeZone);
}

/** "7:00 AM – 10:00 PM", for the tooltip/aria text behind a compact label. */
export function slotRangeLabel(
  start: Date,
  end: Date,
  timeZone: string,
): string {
  return `${clockLabel(start, timeZone)} – ${clockLabel(end, timeZone)}`;
}

/**
 * "Today" / "Tomorrow" / "Sat, 19th September" — resolved in the venue's
 * zone, not the server's, so a late-evening slot never reads as tomorrow to
 * the one person standing outside the building.
 *
 * Beyond tomorrow the weekday alone repeats inside the two-week window
 * ("Fri … Sat … Fri"), so the full date rides along — otherwise the second
 * Friday reads as a duplicate rather than next week.
 */
export function relativeDayLabel(
  at: Date,
  now: Date,
  timeZone: string,
): string {
  const slotDay = getZonedComponents(at, timeZone);
  const today = getZonedComponents(now, timeZone);
  const slotKey = Date.UTC(slotDay.year, slotDay.month - 1, slotDay.day);
  const todayKey = Date.UTC(today.year, today.month - 1, today.day);
  const dayDiff = Math.round((slotKey - todayKey) / 86_400_000);
  if (dayDiff <= 0) return "Today";
  if (dayDiff === 1) return "Tomorrow";
  const date = new Date(slotKey);
  const weekday = date.toLocaleDateString("en-US", {
    weekday: "short",
    timeZone: "UTC",
  });
  const month = date.toLocaleDateString("en-US", {
    month: "long",
    timeZone: "UTC",
  });
  return `${weekday}, ${slotDay.day}${ordinal(slotDay.day)} ${month}`;
}

function ordinal(day: number): string {
  if (day >= 11 && day <= 13) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}
